import { useState } from 'react';
import { UserPlus } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import { Button } from '~/components/ui/button';
import { parentStudentStatus, STUDENT_STATUSES } from '../ParentEnrollment/parentStudentStatus';
import { DISPLAY_TIME_ZONE } from './checklistWords';
import { useAddChildToYear } from './useAddChildToYear';

interface AddChildToYearDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  familyEnrollmentId: string;
  familyName: string;
  yearName: string;
  gradeLevels: { description: string }[];
  /** The year's programs, each with its price. */
  programs?: { key: string; name: string }[];
  /** The year's first day (YYYY-MM-DD); a child added after it starts today unless the office says otherwise. */
  yearStartsOn?: string | null;
  /** Tuition is agreed or the year is enrolled: nobody is left to pick this child's program on Who's coming, so the office does. */
  programRequired?: boolean;
  onSuccess?: () => void;
}

const NEW_FORM = {
  firstName: '',
  lastName: '',
  dateOfBirth: '',
  gender: '',
  gradeLevel: '',
  status: 'ACCEPTED_PENDING_DEPOSIT',
};

const NEW_PROGRAM = '';

// Today as a date, in the zone the office reads dates in.
function todayInOfficeZone(): string {
  return new Date().toLocaleDateString('en-CA', { timeZone: DISPLAY_TIME_ZONE });
}

const selectClass = 'h-9 w-full rounded-md border border-input bg-background px-2 text-sm';

// The office adds a new child to this family's school year. The server's
// answer decides: any refusal stays on screen and the dialog stays open.
export function AddChildToYearDialog({
  open,
  onOpenChange,
  familyEnrollmentId,
  familyName,
  yearName,
  gradeLevels,
  programs = [],
  yearStartsOn = null,
  programRequired = false,
  onSuccess,
}: AddChildToYearDialogProps) {
  // A year that has begun starts the child today; the office can change the date or clear it for the whole year.
  const defaultStart = () => (yearStartsOn && yearStartsOn <= todayInOfficeZone() ? todayInOfficeZone() : '');
  const [form, setForm] = useState({ ...NEW_FORM, program: NEW_PROGRAM, startsOn: defaultStart() });
  const [startsTouched, setStartsTouched] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [addChild, { loading }] = useAddChildToYear();

  const programMissing = programRequired && !form.program;

  const set = (field: keyof typeof form) => (value: string) => setForm((current) => ({ ...current, [field]: value }));

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setForm({ ...NEW_FORM, program: NEW_PROGRAM, startsOn: defaultStart() });
      setStartsTouched(false);
      setErrors([]);
    }
    onOpenChange(next);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const missing = [
      !form.firstName.trim() && 'First name is required',
      !form.lastName.trim() && 'Last name is required',
      !form.gradeLevel && 'Choose a grade',
    ].filter(Boolean) as string[];
    if (missing.length > 0) {
      setErrors(missing);
      return;
    }

    try {
      const result = await addChild({
        variables: {
          familyEnrollmentId,
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          dateOfBirth: form.dateOfBirth || null,
          gender: (form.gender || null) as 'MALE' | 'FEMALE' | null,
          gradeLevel: form.gradeLevel,
          status: form.status as 'ACCEPTED_PENDING_DEPOSIT',
          tuitionOptionKey: form.program || null,
          // Left alone, the server decides (today, once the year has begun); cleared, the child pays for the whole year.
          ...(startsTouched ? { startsOn: form.startsOn || null } : {}),
        },
      });
      const payload = result.data?.addChildToYear;
      const userErrors = payload?.userErrors;
      if (userErrors && userErrors.length > 0) {
        setErrors(userErrors.map((error) => error.message));
        return;
      }
      if (!payload?.studentEnrollment) {
        setErrors(['The child was not added. Please try again.']);
        return;
      }
      handleOpenChange(false);
      onSuccess?.();
    } catch (error) {
      setErrors([error instanceof Error ? error.message : 'The child was not added. Please try again.']);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add a child to this year</DialogTitle>
          <DialogDescription>
            Add a new child of the {familyName} to {yearName}.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {errors.length > 0 && (
            <div role="alert" className="space-y-1 text-sm text-destructive">
              {errors.map((message) => (
                <p key={message}>{message}</p>
              ))}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label htmlFor="add-child-first-name">First name</Label>
              <Input id="add-child-first-name" value={form.firstName} onChange={(e) => set('firstName')(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="add-child-last-name">Last name</Label>
              <Input id="add-child-last-name" value={form.lastName} onChange={(e) => set('lastName')(e.target.value)} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label htmlFor="add-child-dob">Date of birth</Label>
              <Input id="add-child-dob" type="date" value={form.dateOfBirth} onChange={(e) => set('dateOfBirth')(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="add-child-gender">Gender</Label>
              <select id="add-child-gender" className={selectClass} value={form.gender} onChange={(e) => set('gender')(e.target.value)}>
                <option value="">Not given</option>
                <option value="MALE">Male</option>
                <option value="FEMALE">Female</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label htmlFor="add-child-grade">Grade</Label>
              <select id="add-child-grade" className={selectClass} value={form.gradeLevel} onChange={(e) => set('gradeLevel')(e.target.value)}>
                <option value="">Choose a grade</option>
                {gradeLevels.map((grade) => (
                  <option key={grade.description} value={grade.description}>{grade.description}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="add-child-status">Status</Label>
              <select id="add-child-status" className={selectClass} value={form.status} onChange={(e) => set('status')(e.target.value)}>
                {STUDENT_STATUSES.map((status) => (
                  <option key={status} value={status}>{parentStudentStatus(status)}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label htmlFor="add-child-program">Program</Label>
              <select id="add-child-program" className={selectClass} value={form.program} onChange={(e) => set('program')(e.target.value)}>
                <option value="">Not chosen yet</option>
                {programs.map((program) => (
                  <option key={program.key} value={program.key}>{program.name}</option>
                ))}
              </select>
              {programMissing && <p className="text-xs text-muted-foreground">Choose a program: tuition is already agreed for this year</p>}
            </div>
            <div className="space-y-1">
              <Label htmlFor="add-child-starts">Starts</Label>
              <Input
                id="add-child-starts"
                type="date"
                value={form.startsOn}
                onChange={(e) => { setStartsTouched(true); set('startsOn')(e.target.value); }}
              />
              {!form.startsOn && <p className="text-xs text-muted-foreground">Whole year</p>}
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading || programMissing}>
              {loading ? 'Adding...' : 'Add child'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

type AddChildToYearActionProps = Omit<AddChildToYearDialogProps, 'open' | 'onOpenChange'> & {
  checklistEnrollmentOn: boolean;
};

// The office's button for the dialog. Like Invite to enroll, it waits for the
// school's checklist enrollment switch; the server refuses until then too.
export function AddChildToYearAction({ checklistEnrollmentOn, ...dialogProps }: AddChildToYearActionProps) {
  const [open, setOpen] = useState(false);
  if (!checklistEnrollmentOn) return null;

  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)}>
        <UserPlus className="h-4 w-4 mr-2" />
        Add a child to this year
      </Button>
      <AddChildToYearDialog {...dialogProps} open={open} onOpenChange={setOpen} />
    </>
  );
}

export default AddChildToYearDialog;
